import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import type { Template } from '@/types/templates'
import { TemplateProvider } from '@/contexts/TemplateContext'
import { getPrimaryPalette } from '@/lib/templates/registry'
import {
  createTemplatePreviewProfile,
  PREVIEW_BUSINESS_NAME,
  PREVIEW_TAGLINE,
} from '@/lib/templates/templatePreviewFixtures'
import { TemplatePreviewRenderer } from '@/components/dashboard/TemplatePreviewRenderer'
import { cn } from '@/lib/utils'

interface TemplateThumbnailCardProps {
  template: Template
  isSelected: boolean
  colorScheme: string
  typography: string
  onSelect: () => void
  showLivePreview?: boolean
}

function TemplateThumbnailCardComponent({
  template,
  isSelected,
  colorScheme,
  typography,
  onSelect,
  showLivePreview = false,
}: TemplateThumbnailCardProps) {
  const { t } = useTranslation('dashboard')
  const primaryPalette = getPrimaryPalette(template.slug)
  const paletteColors = primaryPalette?.colors
  const label =
    t(`appearance.template.${template.slug}`, { defaultValue: template.name })

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'group rounded-lg border-2 text-left transition-all overflow-hidden bg-card',
        isSelected
          ? 'border-primary ring-2 ring-primary/20 shadow-sm'
          : 'border-border hover:border-primary/40',
      )}
    >
      <div className="aspect-[4/5] w-full overflow-hidden bg-muted/40 relative">
        {showLivePreview ? (
          <TemplateProvider colorScheme={colorScheme} typography={typography}>
            <div className="absolute inset-0 overflow-hidden">
              <div className="transform scale-[0.18] origin-top-left w-[555%] pointer-events-none">
                <TemplatePreviewRenderer
                  templateId={template.id}
                  profile={createTemplatePreviewProfile(template.id, {
                    colorScheme,
                    typography,
                  })}
                />
              </div>
            </div>
          </TemplateProvider>
        ) : (
          <div className="flex h-full flex-col">
            <div
              className="h-10 shrink-0"
              style={{ backgroundColor: paletteColors?.primary ?? '#e5e7eb' }}
            />
            <div className="flex flex-1 flex-col items-center gap-2 px-3 py-4">
              <div
                className="h-10 w-10 rounded-full border-2"
                style={{
                  borderColor: paletteColors?.accent ?? '#d1d5db',
                  backgroundColor: paletteColors?.secondary ?? '#f3f4f6',
                }}
              />
              <div className="w-full space-y-1.5 text-center">
                <div
                  className="mx-auto h-2 w-3/4 rounded-full"
                  style={{ backgroundColor: paletteColors?.text ?? '#374151' }}
                />
                <div
                  className="mx-auto h-1.5 w-1/2 rounded-full opacity-60"
                  style={{
                    backgroundColor: paletteColors?.textMuted ?? '#9ca3af',
                  }}
                />
              </div>
              <div className="mt-auto grid w-full grid-cols-3 gap-1">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="aspect-square rounded-sm"
                    style={{
                      backgroundColor: paletteColors?.secondary ?? '#f3f4f6',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        {isSelected && (
          <div className="absolute top-2 right-2 rounded-full bg-primary p-1 text-primary-foreground shadow-sm">
            <Check className="h-3 w-3" />
          </div>
        )}
      </div>
      <div className="border-t px-3 py-2">
        <p className="text-xs font-medium text-foreground line-clamp-2">{label}</p>
        <p className="mt-0.5 text-[10px] text-muted-foreground line-clamp-1">
          {PREVIEW_BUSINESS_NAME} · {PREVIEW_TAGLINE}
        </p>
      </div>
    </button>
  )
}

export const TemplateThumbnailCard = memo(TemplateThumbnailCardComponent)
